import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { subscriptionApi } from '../../api/subscriptionApi';
import SettingsWrapper from './SettingsWrapper';
import toast from 'react-hot-toast';

const ProfilePage = () => {
    const { user, updateUser } = useAuth();
    const [form, setForm] = useState({ fullName: '', email: '' });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        if (user) setForm({ fullName: user.fullName, email: user.email });
    }, [user]);

    const validate = () => {
        const e: Record<string, string> = {};
        if (!form.fullName.trim()) e.fullName = 'Full name is required';
        if (!form.email.trim()) e.email = 'Email is required';
        else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'Invalid email';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        setLoading(true);
        try {
            const res = await subscriptionApi.updateProfile(form.fullName.trim(), form.email.trim());
            if (res.success) {
                toast.success('Profile updated!');
                if (user) updateUser({ ...user, fullName: form.fullName.trim(), email: form.email.trim() });
            } else toast.error(res.message || 'Update failed');
        } catch { toast.error('Something went wrong'); }
        finally { setLoading(false); }
    };

    return (
        <SettingsWrapper title="Profile" subtitle="Update your name and email address">
            {/* Avatar */}
            <div className="flex items-center gap-4 mb-5 p-4 bg-white border border-gray-100 rounded-xl shadow-sm">
                <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-lg font-bold text-white">{form.fullName?.charAt(0).toUpperCase()}</span>
                </div>
                <div>
                    <p className="text-sm font-bold text-gray-800">{form.fullName}</p>
                    <p className="text-xs text-gray-400">{form.email}</p>
                    {user?.currentPlan && (
                        <span className="inline-block mt-1 text-[10px] font-bold bg-teal-50 text-teal-700 border border-teal-100 px-2 py-0.5 rounded-full">{user.currentPlan} Plan</span>
                    )}
                </div>
            </div>

            <div className="bg-white border border-gray-100 rounded-xl shadow-sm p-6 space-y-4">
                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Full Name</label>
                    <input type="text" value={form.fullName} onChange={e => setForm(f => ({ ...f, fullName: e.target.value }))} placeholder="John Doe"
                        className={`w-full px-4 py-3 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-100 focus:border-teal-400 transition-all text-gray-800 ${errors.fullName ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50 focus:bg-white'}`}/>
                    {errors.fullName && <p className="text-red-500 text-xs mt-1">{errors.fullName}</p>}
                </div>
                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Email Address</label>
                    <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="you@example.com"
                        className={`w-full px-4 py-3 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-100 focus:border-teal-400 transition-all text-gray-800 ${errors.email ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50 focus:bg-white'}`}/>
                    {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                </div>
                <div className="flex justify-end pt-1">
                    <button onClick={handleSubmit} disabled={loading}
                        className="px-6 py-2.5 bg-teal-600 hover:bg-teal-700 text-white text-sm font-bold rounded-xl transition-all disabled:opacity-60 flex items-center gap-2">
                        {loading && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"/>}
                        Save Changes
                    </button>
                </div>
            </div>
        </SettingsWrapper>
    );
};

export default ProfilePage;